import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get("session_id");

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentDetails(data);
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts >= maxAttempts - 1) {
        setStatus("failed");
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }

    pollPaymentStatus();
  }, [sessionId, navigate, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0] flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl" data-testid="payment-status-card">
        <CardContent className="p-8 text-center space-y-6">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-[#04539F] mx-auto animate-spin" data-testid="loading-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="checking-message">Checking Payment Status...</h2>
              <p className="text-gray-600">Please wait while we verify your payment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" data-testid="success-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="success-message">Payment Successful!</h2>
              <p className="text-gray-600">
                Thank you for your purchase. You will receive a confirmation email shortly.
              </p>
              {paymentDetails && (
                <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2" data-testid="payment-details">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Amount:</span> €{paymentDetails.amount_total.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Status:</span> {paymentDetails.payment_status}
                  </p>
                </div>
              )}
              <Button 
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                onClick={() => navigate("/")}
                data-testid="return-home-btn"
              >
                Return to Home
              </Button>
            </>
          )}

          {(status === "failed" || status === "expired") && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto" data-testid="error-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="error-message">
                {status === "expired" ? "Payment Expired" : "Payment Failed"}
              </h2>
              <p className="text-gray-600">
                {status === "expired" 
                  ? "Your payment session has expired. Please try again."
                  : "There was an issue processing your payment. Please try again or contact support."}
              </p>
              <Button 
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                onClick={() => navigate("/")}
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
